import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Form, Input, Button, Card, Typography, App, Space, Row, Col } from 'antd';
import { SafetyOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useAuth } from '@/hooks/useAuth';

const { Title, Text } = Typography;

export function OTPVerify() {
  const { message } = App.useApp();
  const [resendTimer, setResendTimer] = useState(60);
  const navigate = useNavigate();
  const { phoneNumber, verifyOtp, requestOtp, isLoading, error } = useAuth();

  useEffect(() => {
    if (!phoneNumber) {
      navigate('/login');
      return;
    }

    const timer = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [phoneNumber, navigate]);

  const onFinish = async (values: { otp: string }) => {
    if (!phoneNumber) {
      message.error('Phone number not found. Please login again.');
      navigate('/login');
      return;
    }

    const success = await verifyOtp(values.otp);
    if (success) {
      message.success('Login successful!');
      navigate('/dashboard');
    }
  };

  const handleResendOTP = async () => {
    if (!phoneNumber || resendTimer > 0) return;

    const success = await requestOtp(phoneNumber);
    if (success) {
      message.success('OTP resent successfully!');
      setResendTimer(60);
    }
  };

  const handleBack = () => {
    navigate('/login');
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f0f2f5',
      }}
    >
      <Card style={{ width: '100%', maxWidth: 400, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack} style={{ padding: 0 }}>
              Back
            </Button>
          </div>

          <div style={{ textAlign: 'center' }}>
            <Title level={2}>Verify OTP</Title>
            <Text type="secondary">Enter the 6-digit code sent to</Text>
            <br />
            <Text strong>{phoneNumber}</Text>
          </div>

          {error && <Alert type="error" showIcon message={error} />}

          <Form name="otp-verify" onFinish={onFinish} layout="vertical" requiredMark={false}>
            <Form.Item
              label="Verification Code"
              name="otp"
              rules={[
                { required: true, message: 'Please enter the OTP' },
                { len: 6, message: 'OTP must be 6 digits' },
                { pattern: /^\d+$/, message: 'OTP must contain only numbers' },
              ]}
            >
              <Input
                prefix={<SafetyOutlined />}
                placeholder="000000"
                size="large"
                maxLength={6}
                disabled={isLoading}
                style={{ letterSpacing: '0.5em', textAlign: 'center' }}
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={isLoading} size="large" block>
                Verify & Login
              </Button>
            </Form.Item>
          </Form>

          <Row justify="center">
            <Col>
              {resendTimer > 0 ? (
                <Text type="secondary">Resend OTP in {resendTimer} seconds</Text>
              ) : (
                <Button type="link" onClick={handleResendOTP} loading={isLoading}>
                  Resend OTP
                </Button>
              )}
            </Col>
          </Row>
        </Space>
      </Card>
    </div>
  );
}
